import { takeEvery, put } from "redux-saga/effects";
import api from "../../../utils/apiService";
import { ENDPOINT } from "../../../config/endpoints";
import { getInventoryData, saveInventoryData } from "../../slices/InventorySlice";

/**
 * Uplifted from `redux-store/saga/inventory`.
 *
 * 🔴 **`.desktop`-only because mobile's saga was NEVER WIRED IN — not because inventory is a
 * web-only concern.** The distinction matters (hazard 32): the first is a statement about the
 * platform, the second about demand, and only the second is true here. Measured before the
 * uplift, not assumed:
 *
 * - `apps/mobile/src/reduxStore/saga/inventory/inventory.js` existed, but had **zero importers**
 *   and was **absent from mobile's `rootSaga`** — unreachable by two independent routes.
 * - `apps/mobile/src/components/CustomiseItemPopupMb/customiseItemHeader.tsx:73` dispatches
 *   `getInventoryData` into no watcher. That was true before this uplift and is still true after
 *   it, so nothing about mobile's behaviour changes here.
 *
 * The user's decision (2026-08-21) was to delete that dead file rather than preserve a body that
 * provably never runs. **If mobile's `rootSaga` ever wires inventory in, this becomes a
 * three-file split that day** — and mobile's old worker diverged, so it is not a copy of this
 * one: it dispatched `saveInventoryData(response)` flat, with no `itemId` guard, where web keys
 * the response by `itemId`. That difference is real and matches `InventorySlice`, whose
 * `saveInventoryData` reducer is itself split for exactly this reason.
 *
 * ⚠️ Every core import names the VARIANT, not the folder (hazard 6/16).
 */

/**
 * Fetch one item's inventory and store it keyed by item id.
 *
 * ⚠️ `hideLoader` is the apps' own axios flag — read by `apiService`'s interceptors, absent from
 * `AxiosRequestConfig` — so it needs `as any` now that this is a `.ts` file. Same pattern as
 * `skipGlobalLoader` in `saga/releaseNotes` and `saga/printer`.
 *
 * The empty-`itemId` guard and the swallowed `catch` are preserved verbatim: a failed fetch logs
 * and leaves the slice untouched rather than dispatching an empty entry.
 */
function* fetchInventoryData(action: any): Generator<any, void, any> {
  let payload = action.payload;
  try {
    const response = yield api.request({
      method: "GET",
      url: ENDPOINT.INVENTORY.getInventory(payload.itemId, payload.branchId),
      hideLoader: true,
    } as any);
    const itemId = response?.itemId || "";
    if (itemId) {
      yield put(saveInventoryData({ [itemId]: response }));
    }
  } catch (error) {
    console.log("error", error);
  }
}

export default function* root(): Generator<any, void, any> {
  yield takeEvery(getInventoryData.type, fetchInventoryData);
}
